import { useSelector, useDispatch } from "react-redux"
import { useEffect, ReactNode } from "react"
import type { RootState, AppDispatch } from "../store"
import { hideSplash } from "../features/ui/splashSlice"
import { fetchMenuData } from "../features/menu/menuSlice"

import { useUrlParams } from "../utils/urlParams"
import ItemDetailsModal from "./modals/ItemDetailsModal"
import LoginModal from "../features/auth/LoginModal"
import SplashScreen from "./ui/SplashScreen"
import QRParamsGuard from "./ui/QRParamsGuard"
import NetworkGuard from "./ui/NetworkGuard"
import PaymentReturnHandler from "./PaymentReturnHandler"

interface RestaurantAppProps {
  children: ReactNode
}

export default function RestaurantApp({ children }: RestaurantAppProps) {
  const dispatch = useDispatch<AppDispatch>()
  const { isVisible: showSplash, hasShown } = useSelector((state: RootState) => state.splash)
  const { isLoading: menuLoading, categories } = useSelector((state: RootState) => state.menu)

  // Use URL params hook for QR code parameters
  const { getApiParams: getApiParamsFromHook, hasRequiredParams } = useUrlParams()




  // Start loading menu data when app starts - only on first load
  useEffect(() => {
    if (!hasShown && categories.length === 0) {
     
      // Log URL parameters for debugging
      // URL params logging removed

      // Only fetch if we have required parameters
      if (hasRequiredParams()) {
        // Get dynamic parameters from URL using the hook
        const { branchId, typeId } = getApiParamsFromHook()

        // Ensure we have valid parameters before dispatching
        if (branchId && typeId) {
          dispatch(fetchMenuData({ branchId, typeId }))
        }
      }
    } else if (hasShown) {
      // Splash has already been shown, hide it immediately
      dispatch(hideSplash())
    }
  }, [dispatch, hasShown, categories.length, getApiParamsFromHook, hasRequiredParams])

  const handleSplashComplete = () => {
    dispatch(hideSplash())
  }

  // Show splash screen only on very first app load, never on navigation
  if (showSplash && !hasShown && !localStorage.getItem('app_initialized')) {
    return (
      <SplashScreen
        onComplete={() => {
          localStorage.setItem('app_initialized', 'true')
          handleSplashComplete()
        }}
        isLoading={menuLoading}
      />
    )
  }

  return (
    <QRParamsGuard showWarning={false}>
      <NetworkGuard>
        <div className="w-full bg-white min-h-screen relative">
          {children}
          <ItemDetailsModal />
          <LoginModal />
          <PaymentReturnHandler />
        </div>
      </NetworkGuard>
    </QRParamsGuard>
  )
}
